import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiHome, FiUser, FiCode, FiMail, FiMenu, FiX } from 'react-icons/fi';
import './NavigationBar.css';

const RpLogo = ({ size = 36 }) => (
  <svg viewBox="0 0 100 100" width={size} height={size} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="rpNavGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#F05A44" />
        <stop offset="100%" stopColor="#8A344E" />
      </linearGradient>
    </defs>
    <g stroke="url(#rpNavGrad)" strokeWidth="14" strokeLinecap="round" strokeLinejoin="round">
      <path d="M25 20v60M25 20h30c15 0 15 25 0 25H25M55 45l20 35" />
      <path d="M45 20v60M45 20h30c15 0 15 25 0 25H45" opacity="0.8" />
    </g>
  </svg>
);

const Navigation = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const listItems = [
    { id: 'homeBlock', icon: <FiHome />, label: 'Home' },
    { id: 'aboutMeBlock', icon: <FiUser />, label: 'About' },
    { id: 'projectsBlock', icon: <FiCode />, label: 'Projects' },
    { id: 'contactBlock', icon: <FiMail />, label: 'Contact' },
  ];

  const navigateTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    if (isMobile) setIsOpen(false);
  };

  const navItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { delay: i * 0.08, type: 'spring', stiffness: 300, damping: 24 }
    }),
    exit: { opacity: 0, x: -20, transition: { duration: 0.15 } }
  };

  // Desktop: vertically centered dock on the left
  if (!isMobile) {
    return (
      <motion.nav
        className="floating-dock glass-panel"
        initial={{ x: -80, opacity: 0, y: '-50%' }}
        animate={{ x: 0, opacity: 1, y: '-50%' }}
        transition={{ type: 'spring', stiffness: 200, damping: 20, delay: 0.5 }}
      >
        <motion.div
          className="dock-logo"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => navigateTo('homeBlock')}
          role="button"
          tabIndex={0}
          aria-label="Go to top"
        >
          <RpLogo size={32} />
        </motion.div>
        <div className="dock-divider" />
        <ul className="dock-list">
          {listItems.map((item) => (
            <motion.li
              key={item.id}
              className="dock-item"
              whileHover={{ scale: 1.2, x: 5 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => navigateTo(item.id)}
            >
              <span className="dock-icon">{item.icon}</span>
              <span className="dock-tooltip">{item.label}</span>
            </motion.li>
          ))}
        </ul>
      </motion.nav>
    );
  }

  // Mobile: hamburger toggle with animated drawer
  return (
    <>
      <motion.button
        className="hamburger-btn glass-panel"
        onClick={() => setIsOpen(!isOpen)}
        whileTap={{ scale: 0.9 }}
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.span
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="hamburger-icon"
            >
              <FiX />
            </motion.span>
          ) : (
            <motion.span
              key="menu"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="hamburger-icon"
            >
              <FiMenu />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.nav
            className="mobile-drawer glass-panel"
            initial={{ x: -200, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -200, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            <ul className="mobile-nav-list">
              {listItems.map((item, i) => (
                <motion.li
                  key={item.id}
                  className="mobile-nav-item"
                  custom={i}
                  variants={navItemVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigateTo(item.id)}
                >
                  <span className="mobile-nav-icon">{item.icon}</span>
                  <span className="mobile-nav-label">{item.label}</span>
                </motion.li>
              ))}
            </ul>
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;